import { track } from "@/lib/analytics";
import React, { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { ChevronRight, Plus, Printer, Trash2, Sun, Cloud, CloudRain, Wind, Mail } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { escapeDeep, escapeHtml, todayIST } from "@/lib/utils";
import axios from "axios";
import { can } from "@/lib/permissions";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const WEATHER = ["Clear", "Partly Cloudy", "Cloudy", "Light Rain", "Heavy Rain", "Windy"];

const weatherIcon = (w) => {
  if (w?.includes("Rain")) return <CloudRain size={12} className="inline text-blue-500" />;
  if (w?.includes("Cloud")) return <Cloud size={12} className="inline text-slate-400" />;
  if (w === "Windy") return <Wind size={12} className="inline text-teal-500" />;
  return <Sun size={12} className="inline text-amber-500" />;
};

export default function DPRPage() {
  const { id: projectId } = useParams();
  const { user, workspace } = useAuth();
  const [project, setProject] = useState(null);
  const [dprs, setDprs] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    date: todayIST(),
    work_done: "",
    manpower_deployed: "",
    materials_used: "",
    issues_blockers: "",
    weather: "Clear",
    tasks_completed: [],
    photos: []
  });

  const canCreate = can(user, "dpr.edit");
  const canManage = can(user, "dpr.delete");
  const [sendingWeekly, setSendingWeekly] = useState(false);

  const sendWeeklySummary = async () => {
    if (!project?.client_email) {
      toast.error("No client email set for this project. Add it in Project Settings.");
      return;
    }
    setSendingWeekly(true);
    try {
      const { data } = await axios.post(`${API}/dpr/weekly-summary`, { project_id: projectId });
      toast.success(`Weekly summary sent to ${project.client_email} (${data.dpr_count} DPRs, ${data.week_start}–${data.week_end})`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to send weekly summary");
    }
    setSendingWeekly(false);
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [pRes, dRes, tRes] = await Promise.all([
          axios.get(`${API}/projects/${projectId}`),
          axios.get(`${API}/dpr?project_id=${projectId}`),
          axios.get(`${API}/tasks?project_id=${projectId}`)
        ]);
        setProject(pRes.data);
        setDprs(dRes.data);
        // Show non-completed tasks for selection
        setTasks(tRes.data.filter(t => t.status !== "completed" && t.status !== "not_started"));
      } catch (err) { console.error(err); }
      setLoading(false);
    };
    fetchData();
  }, [projectId]);

  const toggleTask = (taskName) => {
    setForm(p => ({
      ...p,
      tasks_completed: p.tasks_completed.includes(taskName)
        ? p.tasks_completed.filter(n => n !== taskName)
        : [...p.tasks_completed, taskName]
    }));
  };

  const saveDPR = async (e) => {
    e.preventDefault();
    if (!form.work_done || !form.manpower_deployed) {
      toast.error("Please fill in required fields: Work Done and Manpower");
      return;
    }
    setSaving(true);
    try {
      const { data } = await axios.post(`${API}/dpr`, {
        project_id: projectId,
        ...form,
        manpower_deployed: parseInt(form.manpower_deployed)
      });
      setDprs(p => [data, ...p]);
      track("dpr_logged", { manpower: Number(data.manpower_deployed || 0), photos: (data.photos || []).length });
      setShowCreate(false);
      setForm({
        date: todayIST(),
        work_done: "", manpower_deployed: "", materials_used: "",
        issues_blockers: "", weather: "Clear", tasks_completed: [], photos: []
      });
      toast.success("DPR saved successfully!");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save DPR");
    }
    setSaving(false);
  };

  const deleteDPR = async (dprId) => {
    if (!window.confirm("Delete this DPR?")) return;
    try {
      await axios.delete(`${API}/dpr/${dprId}`);
      setDprs(p => p.filter(d => d.id !== dprId));
      toast.success("DPR deleted");
    } catch (err) { toast.error(err.response?.data?.detail || "Could not delete the DPR"); }
  };

  const viewDPR = (rawDpr) => {
    const dpr = escapeDeep(rawDpr);                 // never interpolate raw user text into HTML
    const ws = escapeDeep(workspace || {});
    const baseUrl = process.env.REACT_APP_BACKEND_URL;
    const logoHtml = ws.logo_url
      ? `<img src="${baseUrl}${ws.logo_url}" alt="logo" style="height:50px;width:auto;object-fit:contain;background:white;padding:3px;border-radius:3px;margin-right:12px">`
      : "";

    const tasksHtml = dpr.tasks_completed?.length > 0
      ? `<ul style="margin:6px 0 0 16px;padding:0">${dpr.tasks_completed.map(t => `<li style="margin:3px 0;color:#1e293b;font-size:11px">${t}</li>`).join("")}</ul>`
      : `<span style="color:#94a3b8;font-size:11px">None recorded</span>`;

    const dateStr = new Date(dpr.date + "T00:00:00").toLocaleDateString("en-IN", {
      weekday: "long", day: "numeric", month: "long", year: "numeric"
    });

    const html = `<!DOCTYPE html><html><head>
      <meta charset="UTF-8">
      <title>DPR — ${dateStr}</title>
      <style>
        * { margin:0; padding:0; box-sizing:border-box; }
        body { font-family: 'Segoe UI', Arial, sans-serif; font-size:12px; color:#1e293b; background:#fff; }
        .page { max-width:800px; margin:0 auto; padding:32px; }
        .section { margin-bottom:16px; padding:12px 16px; border:1px solid #e2e8f0; border-radius:6px; }
        .section-title { font-size:10px; font-weight:700; text-transform:uppercase; letter-spacing:0.06em; color:#64748b; margin-bottom:8px; }
        .section-value { font-size:12px; color:#1e293b; line-height:1.5; }
        .grid2 { display:grid; grid-template-columns:1fr 1fr; gap:12px; }
        @media print { body { padding:0; } .no-print { display:none; } .page { padding:16px; max-width:100%; } @page { margin:15mm; } }
      </style>
    </head><body>
      <div class="page">
        <div class="no-print" style="margin-bottom:20px;text-align:right">
          <button onclick="window.print()" style="background:#FFB703;border:none;padding:8px 20px;font-size:13px;font-weight:600;cursor:pointer;border-radius:4px">Print / Save as PDF</button>
        </div>

        <!-- Header -->
        <div style="display:flex;justify-content:space-between;align-items:flex-start;padding:16px;background:#0A1128;color:white;border-radius:6px 6px 0 0">
          <div style="display:flex;align-items:center">
            ${logoHtml}
            <div>
              <div style="font-size:18px;font-weight:700">${ws.name || "Company Name"}</div>
              ${ws.gstin ? `<div style="font-size:11px;color:#94a3b8;margin-top:2px">GSTIN: ${ws.gstin}</div>` : ""}
            </div>
          </div>
          <div style="text-align:right">
            <div style="font-size:20px;font-weight:800;color:#FFB703">DAILY PROGRESS REPORT</div>
            <div style="font-size:13px;color:#cbd5e1;margin-top:4px">${escapeHtml(project?.name || "")}</div>
            <div style="font-size:11px;color:#94a3b8;margin-top:2px">${dateStr}</div>
          </div>
        </div>

        <!-- Meta -->
        <div style="display:grid;grid-template-columns:repeat(4,1fr);gap:0;border:1px solid #e2e8f0;border-top:3px solid #FFB703;margin-bottom:16px">
          <div style="padding:10px 12px;border-right:1px solid #e2e8f0">
            <div style="font-size:9px;color:#94a3b8;text-transform:uppercase;font-weight:700;margin-bottom:3px">Reported By</div>
            <div style="font-size:12px;font-weight:600">${dpr.engineer_name}</div>
          </div>
          <div style="padding:10px 12px;border-right:1px solid #e2e8f0">
            <div style="font-size:9px;color:#94a3b8;text-transform:uppercase;font-weight:700;margin-bottom:3px">Manpower</div>
            <div style="font-size:12px;font-weight:600">${dpr.manpower_deployed} Persons</div>
          </div>
          <div style="padding:10px 12px;border-right:1px solid #e2e8f0">
            <div style="font-size:9px;color:#94a3b8;text-transform:uppercase;font-weight:700;margin-bottom:3px">Weather</div>
            <div style="font-size:12px;font-weight:600">${dpr.weather}</div>
          </div>
          <div style="padding:10px 12px">
            <div style="font-size:9px;color:#94a3b8;text-transform:uppercase;font-weight:700;margin-bottom:3px">Location</div>
            <div style="font-size:12px;font-weight:600">${escapeHtml(project?.location || "—")}</div>
          </div>
        </div>

        <!-- Work Done -->
        <div class="section">
          <div class="section-title">Work Done Today</div>
          <div class="section-value" style="white-space:pre-line">${dpr.work_done}</div>
        </div>

        <!-- Tasks Completed -->
        <div class="section">
          <div class="section-title">Tasks Completed (${dpr.tasks_completed?.length || 0})</div>
          <div class="section-value">${tasksHtml}</div>
        </div>

        <div class="grid2">
          <!-- Materials Used -->
          <div class="section">
            <div class="section-title">Materials Used</div>
            <div class="section-value" style="white-space:pre-line">${dpr.materials_used || "—"}</div>
          </div>

          <!-- Issues / Blockers -->
          <div class="section" style="${dpr.issues_blockers ? "border-color:#fca5a5;background:#fff5f5" : ""}">
            <div class="section-title" style="${dpr.issues_blockers ? "color:#dc2626" : ""}">Issues / Blockers</div>
            <div class="section-value" style="white-space:pre-line;${dpr.issues_blockers ? "color:#dc2626" : ""}">${dpr.issues_blockers || "None"}</div>
          </div>
        </div>

        <!-- Signature -->
        <div style="margin-top:32px;display:flex;justify-content:space-between;align-items:flex-end">
          <div style="font-size:10px;color:#94a3b8">Generated: ${new Date().toLocaleString("en-IN")}</div>
          <div style="text-align:center;border-top:1px solid #cbd5e1;padding-top:8px;width:180px;font-size:11px;color:#475569">
            <strong>${dpr.engineer_name}</strong><br>Field Engineer
          </div>
        </div>
      </div>
    </body></html>`;

    const blob = new Blob([html], { type: "text/html" });
    const url = URL.createObjectURL(blob);
    const win = window.open(url, "_blank");
    if (!win) toast.error("Please allow popups for this site to open print preview");
    setTimeout(() => URL.revokeObjectURL(url), 60000);
  };

  if (loading) return <div className="p-8 text-[#64748B]">Loading DPRs...</div>;

  return (
    <div className="p-6 animate-fade-up">
      <div className="flex items-center gap-2 text-xs text-[#64748B] mb-4">
        <Link to="/app/projects" className="hover:text-[#FFB703]">Projects</Link>
        <ChevronRight size={12} />
        <Link to={`/app/projects/${projectId}`} className="hover:text-[#FFB703]">{project?.name}</Link>
        <ChevronRight size={12} />
        <span className="text-[#0A1128] font-medium">Daily Progress Reports</span>
      </div>

      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-heading font-bold text-xl text-[#0A1128]">Daily Progress Reports</h1>
          <p className="text-sm text-[#64748B]">{dprs.length} report{dprs.length !== 1 ? "s" : ""} logged</p>
        </div>
        <div className="flex items-center gap-2">
          {canManage && project?.client_email && (
            <button
              onClick={sendWeeklySummary}
              disabled={sendingWeekly}
              className="flex items-center gap-2 border border-[#E2E8F0] text-[#64748B] hover:text-[#0A1128] hover:border-[#0A1128] px-3 py-2 rounded text-xs font-medium transition-colors disabled:opacity-50"
              title={`Send weekly DPR summary to ${project.client_email}`}
              data-testid="send-weekly-summary-btn"
            >
              <Mail size={13} />
              {sendingWeekly ? "Sending..." : "Weekly Email"}
            </button>
          )}
          {canCreate && (
            <button onClick={() => setShowCreate(true)} className="flex items-center gap-2 bg-[#FFB703] text-[#0A1128] font-semibold px-4 py-2 rounded text-sm hover:bg-[#E8A200] transition-colors active:scale-[0.98]" data-testid="create-dpr-btn">
              <Plus size={14} />Log Today's DPR
            </button>
          )}
        </div>
      </div>

      {dprs.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-20 text-center bg-white border border-[#E2E8F0] rounded">
          <Sun size={36} className="text-[#FFB703] mb-3" />
          <p className="text-sm font-medium text-[#0A1128] mb-1">No DPRs logged yet</p>
          <p className="text-xs text-[#64748B] mb-4">Field engineers can log daily progress reports here</p>
          {canCreate && <button onClick={() => setShowCreate(true)} className="text-xs text-[#FFB703] hover:underline font-medium">Log the first DPR</button>}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {dprs.map(dpr => (
            <div key={dpr.id} className="bg-white border border-[#E2E8F0] rounded p-4 hover:border-[#FFB703]/50 hover:shadow-sm transition-all" data-testid={`dpr-${dpr.id}`}>
              <div className="flex items-start justify-between mb-3">
                <div>
                  <div className="font-heading font-semibold text-sm text-[#0A1128]">
                    {new Date(dpr.date + "T00:00:00").toLocaleDateString("en-IN", { weekday: "short", day: "numeric", month: "short", year: "numeric" })}
                  </div>
                  <div className="text-xs text-[#64748B] mt-0.5">By {dpr.engineer_name}</div>
                </div>
                <div className="flex items-center gap-1 text-xs bg-[#F8FAFC] border border-[#E2E8F0] px-2 py-0.5 rounded">
                  {weatherIcon(dpr.weather)} <span className="ml-1">{dpr.weather}</span>
                </div>
              </div>
              <div className="space-y-1.5 text-xs text-[#64748B] mb-3">
                <div>
                  <span className="font-medium text-[#0A1128]">Work Done: </span>
                  <span className="line-clamp-2">{dpr.work_done}</span>
                </div>
                <div className="flex items-center gap-3">
                  <span><span className="font-medium text-[#0A1128]">Manpower:</span> {dpr.manpower_deployed} persons</span>
                  {dpr.tasks_completed?.length > 0 && (
                    <span className="bg-emerald-50 text-emerald-700 px-1.5 py-0.5 rounded text-[10px] font-bold">{dpr.tasks_completed.length} task{dpr.tasks_completed.length !== 1 ? "s" : ""}</span>
                  )}
                </div>
                {dpr.issues_blockers && (
                  <div className="text-orange-600 bg-orange-50 rounded px-2 py-1">
                    <span className="font-medium">Issue: </span>{dpr.issues_blockers}
                  </div>
                )}
              </div>
              <div className="flex items-center gap-2 border-t border-[#F1F5F9] pt-2">
                <button onClick={() => viewDPR(dpr)} className="flex items-center gap-1.5 text-xs text-[#FFB703] hover:text-[#E8A200] font-medium" data-testid={`view-dpr-${dpr.id}`}>
                  <Printer size={11} />Print / View
                </button>
                {canManage && (
                  <button onClick={() => deleteDPR(dpr.id)} className="ml-auto text-[#94A3B8] hover:text-red-500 transition-colors">
                    <Trash2 size={12} />
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Create DPR Modal */}
      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading font-bold text-[#0A1128]">Log Daily Progress Report</DialogTitle>
          </DialogHeader>
          <form onSubmit={saveDPR} className="space-y-4 py-2">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div>
                <label className="block text-xs font-semibold text-[#64748B] uppercase tracking-wider mb-1.5">Date *</label>
                <input type="date" required value={form.date} onChange={e => setForm(p => ({ ...p, date: e.target.value }))} className="w-full border border-[#E2E8F0] rounded px-3 py-2.5 text-sm focus:outline-none focus:ring-1 focus:ring-[#FFB703]" data-testid="dpr-date" />
              </div>
              <div>
                <label className="block text-xs font-semibold text-[#64748B] uppercase tracking-wider mb-1.5">Manpower (Persons) *</label>
                <input type="number" required min="1" value={form.manpower_deployed} onChange={e => setForm(p => ({ ...p, manpower_deployed: e.target.value }))} placeholder="e.g. 25" className="w-full border border-[#E2E8F0] rounded px-3 py-2.5 text-sm focus:outline-none focus:ring-1 focus:ring-[#FFB703]" data-testid="dpr-manpower" />
              </div>
              <div>
                <label className="block text-xs font-semibold text-[#64748B] uppercase tracking-wider mb-1.5">Weather</label>
                <select value={form.weather} onChange={e => setForm(p => ({ ...p, weather: e.target.value }))} className="w-full border border-[#E2E8F0] rounded px-3 py-2.5 text-sm focus:outline-none focus:ring-1 focus:ring-[#FFB703] bg-white">
                  {WEATHER.map(w => <option key={w}>{w}</option>)}
                </select>
              </div>
            </div>

            <div>
              <label className="block text-xs font-semibold text-[#64748B] uppercase tracking-wider mb-1.5">Work Done Today *</label>
              <textarea required value={form.work_done} onChange={e => setForm(p => ({ ...p, work_done: e.target.value }))} rows={3} placeholder="Describe all work completed today in detail..." className="w-full border border-[#E2E8F0] rounded px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-[#FFB703] resize-none" data-testid="dpr-work-done" />
            </div>

            {/* Task Selection */}
            {tasks.length > 0 && (
              <div>
                <label className="block text-xs font-semibold text-[#64748B] uppercase tracking-wider mb-1.5">
                  Tasks Completed Today ({form.tasks_completed.length} selected)
                </label>
                <div className="border border-[#E2E8F0] rounded divide-y divide-[#F1F5F9] max-h-48 overflow-y-auto">
                  {tasks.map(t => (
                    <label key={t.id} className="flex items-center gap-3 px-3 py-2 hover:bg-[#F8FAFC] cursor-pointer">
                      <input
                        type="checkbox"
                        checked={form.tasks_completed.includes(t.name)}
                        onChange={() => toggleTask(t.name)}
                        className="w-4 h-4 accent-[#FFB703]"
                      />
                      <span className="text-xs text-[#0A1128]">{t.name}</span>
                      <span className={`ml-auto text-[9px] font-bold uppercase px-1.5 py-0.5 rounded-full ${t.status === "in_progress" ? "bg-blue-100 text-blue-700" : "bg-amber-100 text-amber-700"}`}>
                        {t.status?.replace("_", " ")}
                      </span>
                    </label>
                  ))}
                </div>
              </div>
            )}

            <div>
              <label className="block text-xs font-semibold text-[#64748B] uppercase tracking-wider mb-1.5">Materials Used</label>
              <textarea value={form.materials_used} onChange={e => setForm(p => ({ ...p, materials_used: e.target.value }))} rows={2} placeholder="e.g. 20 solar panels (400W), 50m DC cable..." className="w-full border border-[#E2E8F0] rounded px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-[#FFB703] resize-none" />
            </div>

            <div>
              <label className="block text-xs font-semibold text-[#64748B] uppercase tracking-wider mb-1.5">Issues / Blockers</label>
              <textarea value={form.issues_blockers} onChange={e => setForm(p => ({ ...p, issues_blockers: e.target.value }))} rows={2} placeholder="Any problems, safety issues, or blockers today..." className="w-full border border-[#E2E8F0] rounded px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-[#FFB703] resize-none" />
            </div>

            <div className="flex justify-end gap-3 pt-2">
              <button type="button" onClick={() => setShowCreate(false)} className="px-4 py-2 border border-[#E2E8F0] rounded text-sm text-[#64748B] hover:bg-slate-50">Cancel</button>
              <button type="submit" disabled={saving} className="bg-[#FFB703] text-[#0A1128] font-semibold px-5 py-2 rounded text-sm hover:bg-[#E8A200] transition-colors disabled:opacity-60" data-testid="save-dpr-btn">
                {saving ? "Saving..." : "Save DPR"}
              </button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
